'use client';

import Link from 'next/link';
import { Info, Mail, Lock, ChevronRight } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { FormEvent, useState } from 'react';
import { Button } from '@/components/ui/button';

interface PasswordResetFormProps {
  mode: 'request' | 'confirm';
  token?: string;
}

interface FormStatus {
  type: 'error' | 'success';
  message: string;
}

export function PasswordResetForm({ mode, token }: PasswordResetFormProps) {
  const isRequest = mode === 'request';
  const router = useRouter();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState<FormStatus | null>(null);

  const requestBody = isRequest
    ? JSON.stringify({ email })
    : JSON.stringify({ token, password, confirmPassword });

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStatus(null);

    if (isRequest) {
      if (!email) {
        setStatus({ type: 'error', message: 'Email is required.' });
        return;
      }
    } else {
      if (!token) {
        setStatus({ type: 'error', message: 'A reset token is required.' });
        return;
      }
      if (!password || !confirmPassword) {
        setStatus({ type: 'error', message: 'Both password fields are required.' });
        return;
      }
      if (password.length < 8) {
        setStatus({ type: 'error', message: 'Password must be at least 8 characters.' });
        return;
      }
      if (password !== confirmPassword) {
        setStatus({ type: 'error', message: 'Passwords do not match.' });
        return;
      }
    }

    setIsLoading(true);
    try {
      const response = await fetch(isRequest ? '/api/auth/reset-password/request' : '/api/auth/reset-password/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: requestBody,
      });

      const data = (await response.json().catch(() => ({}))) as {
        message?: string;
        error?: string;
      };

      if (!response.ok) {
        setStatus({
          type: 'error',
          message: data.error || data.message || 'Request could not be completed.',
        });
        return;
      }

      const message =
        data.message ||
        (isRequest
          ? 'If an account exists with this email, a reset link has been sent.'
          : 'Your password has been reset. Redirecting to sign in.');
      setStatus({ type: 'success', message });

      if (!isRequest) {
        setTimeout(() => {
          router.push('/auth/login');
        }, 1500);
      }
    } catch (error) {
      console.error('[PASSWORD RESET FORM] Request failed:', error);
      setStatus({
        type: 'error',
        message: 'Could not connect right now. Please try again in a moment.',
      });
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="w-full">
      <form className="space-y-6" onSubmit={handleSubmit}>
        {isRequest ? (
          <div className="space-y-2">
            <label htmlFor="email" className="block text-sm font-medium text-gray-300">
              Email Address
            </label>
            <div className="relative group">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 group-focus-within:text-purple-400 transition-colors" />
              <input
                id="email"
                type="email"
                autoComplete="email"
                required={isRequest}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-10 py-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all"
                placeholder="name@example.com"
                disabled={isLoading}
              />
            </div>
          </div>
        ) : null}

        {!isRequest ? (
          <>
            <div className="space-y-2">
              <label htmlFor="password" className="block text-sm font-medium text-gray-300">
                New Password
              </label>
              <div className="relative group">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 group-focus-within:text-purple-400 transition-colors" />
                <input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-10 py-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all"
                  placeholder="••••••••"
                  disabled={isLoading}
                />
              </div>
            </div>
            <div className="space-y-2">
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300">
                Confirm New Password
              </label>
              <div className="relative group">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 group-focus-within:text-purple-400 transition-colors" />
                <input
                  id="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-10 py-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all"
                  placeholder="••••••••"
                  disabled={isLoading}
                />
              </div>
            </div>
          </>
        ) : null}

        <Button
          type="submit"
          disabled={isLoading}
          className="w-full py-6 rounded-xl bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-400 hover:to-blue-400 text-white font-bold text-lg shadow-xl shadow-purple-500/20 transition-all"
        >
          {isLoading ? (
            <span className="flex items-center gap-2">
              <span className="animate-spin h-4 w-4 border-2 border-white/30 border-t-white rounded-full" />
              Processing...
            </span>
          ) : (
            <span className="flex items-center gap-2">
              {isRequest ? 'Send Reset Link' : 'Set New Password'}
              <ChevronRight className="h-5 w-5" />
            </span>
          )}
        </Button>

        {status ? (
          <div className={`p-4 rounded-xl text-sm ${
            status.type === 'error' 
              ? 'bg-red-500/10 border border-red-500/20 text-red-400' 
              : 'bg-green-500/10 border border-green-500/20 text-green-400'
          }`}>
            <p className="flex items-center gap-2">
              <Info className="h-4 w-4 shrink-0" />
              {status.message}
            </p>
          </div>
        ) : null}
      </form>

      {!isRequest && !token ? (
        <div className="mt-4 p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
          <p className="text-sm text-red-400 flex items-center gap-2">
            <Info className="h-4 w-4" />
            Token missing. Please use the full link from your email.
          </p>
        </div>
      ) : null}

      <div className="mt-8 text-center">
        <Link 
          href="/auth/login" 
          className="text-sm text-gray-400 hover:text-white transition-colors underline underline-offset-4"
        >
          Back to sign in
        </Link>
      </div>
    </div>
  );
}
